import React, { useEffect, useState } from "react";
import fetch from "./shared/Fetch";
import { format } from "./shared/NumberUtil";
import AppHeading from "./sharedComponents/AppHeading";
import Spinner from "./sharedComponents/Spinner";
import AuthorizedContent from "./sharedComponents/AuthorizedContent";
import styled from "styled-components";
import FilteringBar from "./sharedComponents/FilteringBar";
import { SumData, WeekData } from "./Consultants";
import Table from "./sharedComponents/Table";
import { Link, NavLink } from "react-router-dom";
import FileUpload from "./sharedComponents/FileUpload";
import { ReactComponent as csvIcon } from "./svgs/CsvIcon.svg";
import { CSVLink } from "react-csv";
import TooltipLegend from "./sharedComponents/Tooltip";
import {
  FilteringBarContainer,
  FilterSidebarContainer,
  FilterIconStyled,
  FilterButton,
  CloseButton,
  CloseIconStyled,
  MainLayout
} from "./sharedComponents/FilterSidebar";

const ReconContainer = styled.div`
  padding: 0 5rem 5rem;
  width: 100%;
`;

const ConsultantName = styled.td`
  padding: 0.5rem;
  width: 15%;

  @media (max-width: 900px) {
    width: 30%;
  }

  @media (max-width: 750px) {
    padding: 0;
    font-size: 0.8rem;
  }
`;

const ConsultantRow = styled.tr`
  &:nth-child(2n) {
    background: var(--table-band-background);
  }
  &:hover {
    background: var(--table-hover-background);
  }
`;

const SumRow = styled.tr`
  font-weight: bold;
`;

const Actuals = styled.div`
  color: ${props => (props.warning ? "red" : "DodgerBlue")};
`;

const Staffed = styled.div`
  color: black;
`;

const Worked = styled.div`
  color: MediumSeaGreen;
`;

const BarContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const LeftButtons = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
`;

const SeniorityLabel = styled.div`
  font-size: 0.75rem;
  color: var(--color-lightergrey);
  margin-top: 0.1rem;
`;

const CSVIconStyled = styled(csvIcon)`
  height: 3rem;
  width: 3rem;
  padding-top: 1px;
  & * {
    fill: var(--button-abort-color);
  }
`;

const SumFooter = styled.tfoot`
  position: sticky;
  bottom: 0px;
  background: #fff;
`;

const submitActualStaffing = async rows => {
  const response = await fetch("/actualstaffings", {
    method: "POST",
    body: JSON.stringify({
      rows: rows
    })
  });
  return response;
};

const ReconCell = ({ week, worked }) => {
  const actuals = week.actuals ? week.actuals : 0;
  const staffed = week.staffed ? week.staffed : 0;
  const hasWorked = worked !== null && worked !== undefined;
  const workedHours = hasWorked ? worked : null;
  let warning = true;
  if (staffed === 0 || actuals / staffed > 0.9) warning = false;

  return (
    <WeekData className="tooltip">
      <Staffed>{format(staffed)}</Staffed>
      <Actuals warning={warning}>{format(actuals)}</Actuals>
      <Worked>{hasWorked ? format(workedHours) : "-"}</Worked>

      <TooltipLegend
        items={[
          { value: format(staffed), label: "Planlagt fakturert" },
          { value: format(actuals), label: "Faktisk fakturert" },
          {
            value: hasWorked ? format(workedHours) : "-",
            label: "Totale arbeidstimer"
          }
        ]}
      />
    </WeekData>
  );
};

const ReconLine = ({ cons, uploadedWorked, seniorityById }) => {
  const weekCells = [];
  for (const [key, value] of Object.entries(cons.weeks)) {
    const hasOverride =
      uploadedWorked &&
      uploadedWorked[cons.id] &&
      uploadedWorked[cons.id][key] != null;

    const persistedWorked =
      value.actualWorked != null ? value.actualWorked : null; // only treat as data if actualWorked exists

    const displayWorked = hasOverride
      ? uploadedWorked[cons.id][key]
      : persistedWorked; // may be null

    weekCells.push(<ReconCell key={key} week={value} worked={displayWorked} />);
  }

  const path = window.location.pathname;
  const pattern = /\/(.+?)\/(.+?)\/(.+?)\/(.+?)\/(.+?)\/(.+?)\/(.+?)\/(.+)/g;

  const params = [...path.matchAll(pattern)][0];
  const deps = params[1];
  const groups = params[2];
  const seniorities = params[3];
  const sareas = params[4];
  const inner = "consultant";
  const weeks = params[6];
  const offset = params[7];
  const availableOnly = params[8];

  const to =
    `/${deps}/${groups}/${seniorities}/${sareas}/${inner}/${cons.id}/${weeks}/${offset}/${availableOnly}` +
    window.location.search;
  return (
    <ConsultantRow>
      <ConsultantName>
        <Link to={to}>{cons.name}</Link>
        {(cons.seniorityId || cons.seniorityOrderId) &&
          seniorityById[cons.seniorityId || cons.seniorityOrderId] && (
            <SeniorityLabel>
              {seniorityById[cons.seniorityId || cons.seniorityOrderId]}
            </SeniorityLabel>
          )}
      </ConsultantName>
      {weekCells}
    </ConsultantRow>
  );
};

const Container = styled.nav`
  padding: 2rem 0;
  margin-bottom: 2rem;
`;

export const PageHeading = ({
  departments,
  weeks,
  offset,
  groups,
  seniorities,
  serviceareas,
  availableOnly
}) => {
  const qs = window.location.search;
  const sa = serviceareas || "all";
  let withData = inner =>
    `/${departments}/${groups}/${seniorities}/${sa}/${inner}/${weeks}/${offset}/${availableOnly}` +
    qs;

  return (
    <Container>
      <ul className="hero__subMenuItems">
        <li>
          <NavLink
            className={"hero__submenuItem"}
            to={withData("reconciliation")}
          >
            Avstemming
          </NavLink>
        </li>
        <li>
          <NavLink className={"hero__submenuItem"} to={withData("reconreport")}>
            Rapport
          </NavLink>
        </li>
        <li>
          <NavLink
            className={"hero__submenuItem"}
            to={withData("reconciliationinsights")}
          >
            Innsikt leveringsgrad
          </NavLink>
        </li>
      </ul>
    </Container>
  );
};

const Reconciliation = props => {
  const [data, setData] = useState([]);
  const [waitingForData, setWaitingForData] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [error, setError] = useState();
  const [successMessage, setSuccessMessage] = useState();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  // Holds uploaded actual worked hours (from Excel column F), keyed by consultantId -> yearWeek -> hours
  const [uploadedWorked, setUploadedWorked] = useState({});
  const [isAllowed, setIsAllowed] = useState(false);
  const [isUserChecked, setIsUserChecked] = useState(false);
  const [sortBy, setSortBy] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);
  const [seniorityById, setSeniorityById] = useState({});
  const [groupServiceAreaMap, setGroupServiceAreaMap] = useState({});

  const weeks = props.match.params.weeks;
  const offset = props.match.params.offset;
  const departments = props.match.params.departments;
  const groups = props.match.params.groups;
  const seniorities = props.match.params.seniorities;
  const serviceareas = props.match.params.serviceareas;
  const availableOnly = props.match.params.availableOnly;

  const uploadExcelRows = async rows => {
    setWaitingForData(true);

    const res = await submitActualStaffing(rows);
    console.log(res);

    // Overlay the uploaded actual worked hours for the week (if provided by API)
    if (res && res.workedImported && Array.isArray(res.workedImported)) {
      const map = {};
      for (const entry of res.workedImported) {
        if (!map[entry.consultantId]) map[entry.consultantId] = {};
        map[entry.consultantId][entry.yearWeek] = entry.hours;
      }
      setUploadedWorked(map);
    } else {
      setUploadedWorked({});
    }

    setLastUpdated(new Date());
    setWaitingForData(false);

    if (res.status === "ok") {
      setSuccessMessage(`Status: OK. ${res.count} ble importert.`);
    } else {
      if (res.type === "noMatch") {
        setError({
          updateErrors: res.errors,
          message: `Status: Delvis OK. ${res.count} ble importert.  Fant ikke match på følgende navn. Sjekk at de ligger registrert korrekt i systemet`
        });
      }
      if (res.type === "noCompleteWeek") {
        setError({
          updateErrors: [],
          message:
            "FEIL: Utvalget representer ikke én fullstendig uke. Ingen ble importert"
        });
      }
      if (res.type === "misformatted") {
        setError({
          updateErrors: [],
          message: `FEIL: Importfilen er feilformatert. ${res.count} ble importert. `
        });
      }
    }
  };

  const fetchData = async () => {
    setWaitingForData(true);
    try {
      const responseData = await fetch(
        `/reconciliation/offset/${offset}/weeks/${weeks}`
      );
      setData(Object.values(responseData));
    } catch (e) {
      console.error("Failed to fetch reconciliation data", e);
    } finally {
      setWaitingForData(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [weeks, offset, lastUpdated]);

  useEffect(() => {
    // Fetch seniority names for toggle display
    (async () => {
      try {
        const res = await fetch(`/seniorities`);
        const map = {};
        if (Array.isArray(res)) {
          res.forEach(s => {
            map[s.id] = s.name;
          });
        }
        setSeniorityById(map);
      } catch (e) {
        // ignore
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`/groups`);
        const map = {};
        if (Array.isArray(res)) {
          res.forEach(g => {
            map[g.id] = g.serviceAreaId;
          });
        }
        setGroupServiceAreaMap(map);
      } catch (e) {
        // ignore
      }
    })();
  }, []);

  useEffect(() => {
    // Check access based on Access table for the reconciliation view
    (async () => {
      try {
        const resp = await fetch(`/access?item=RECONCILIATION_VIEW`);
        const allowed = resp && resp.allowed === true;
        setIsAllowed(allowed);
      } catch (e) {
        setIsAllowed(false);
      } finally {
        setIsUserChecked(true);
      }
    })();
  }, []);

  useEffect(() => {
    setWaitingForData(false);
  }, [serviceareas, groups]);

  const departmentsArray = departments === "all" ? [] : departments.split(",");
  const groupsArray = groups === "all" ? [] : groups.split(",");
  const senioritiesArray = seniorities === "all" ? [] : seniorities.split(",");
  const serviceareasArray =
    serviceareas === "all" ? [] : serviceareas ? serviceareas.split(",") : [];

  const sp = new URLSearchParams(props.location.search || "");
  const getQueryParam = key => (sp.get(key) || "").toLowerCase();
  const wantVacation = getQueryParam("vacation") === "true";
  const wantOnLeave = getQueryParam("onleave") === "true";
  const wantOverbooked = getQueryParam("overbooked") === "true";
  const wantHideFullAbsence = getQueryParam("hideFullAbsence") === "true";

  const filteredData = data
    .filter(element => {
      return departmentsArray.some(op => {
        return op === element.departmentId;
      });
    })
    .filter(element => {
      if (groupsArray.length === 0) return true;
      return element.groups.some(g => groupsArray.some(a => a === g.id));
    })
    .filter(element => {
      if (senioritiesArray.length === 0) return true;

      if (!element.seniorityOrderId) return false;

      return senioritiesArray.some(
        s => s === element.seniorityOrderId.toString()
      );
    })
    .filter(element => {
      if (serviceareasArray.length === 0) return true;
      return element.groups.some(g =>
        serviceareasArray.some(a => a === groupServiceAreaMap[g.id])
      );
    })
    .filter(element => {
      if (!(wantVacation || wantOnLeave || wantOverbooked)) return true;
      const weeks = element.weeks ? Object.values(element.weeks) : [];
      const hasVacation = weeks.some(w => (w.vacationHours || 0) > 0);
      const hasLeave = weeks.some(w => (w.plannedAbsenceHours || 0) > 0);
      const hasOver = weeks.some(
        w => (parseFloat(w.staffed) || 0) > (parseFloat(w.totalWorkHours) || 0)
      );
      if (wantVacation && !hasVacation) return false;
      if (wantOnLeave && !hasLeave) return false;
      if (wantOverbooked && !hasOver) return false;
      return true;
    })
    .filter(element => {
      if (!wantHideFullAbsence) return true;
      const weeks = element.weeks ? Object.values(element.weeks) : [];
      const relevantWeeks = weeks.filter(w => (w.totalWorkHours || 0) > 0);
      if (relevantWeeks.length === 0) return true;
      return !relevantWeeks.every(
        w =>
          (w.vacationHours || 0) + (w.plannedAbsenceHours || 0) >=
          (w.totalWorkHours || 0)
      );
    })
    .sort((a, b) => {
      if (a.name > b.name) return 1;
      return -1;
    });

  const yearWeeks = [];
  if (data.length > 0) {
    for (const v of Object.keys(data[0].weeks)) {
      yearWeeks.push(v);
    }
  }

  let sortedData = filteredData;
  if (sortBy !== null && sortDirection !== null) {
    const yw = yearWeeks[sortBy];
    if (yw) {
      sortedData = [...filteredData].sort((a, b) => {
        const aw = (a.weeks && a.weeks[yw]) || {};
        const bw = (b.weeks && b.weeks[yw]) || {};
        const aVal = aw.actuals != null ? parseFloat(aw.actuals) : 0;
        const bVal = bw.actuals != null ? parseFloat(bw.actuals) : 0;
        return sortDirection === "desc" ? bVal - aVal : aVal - bVal;
      });
    }
  }

  const sums = [];
  for (const row of sortedData) {
    let i = 0;
    for (const w of Object.values(row.weeks)) {
      if (!sums[i]) {
        sums[i] = {
          actuals: 0,
          staffed: 0,
          totalAvailableHours: 0,
          plannedAbsenceHours: 0,
          vacationHours: 0
        };
      }

      sums[i].actuals += w.actuals ? parseFloat(w.actuals) : 0;
      sums[i].staffed += w.staffed ? parseFloat(w.staffed) : 0;
      sums[i].totalAvailableHours += w.totalWorkHours
        ? parseFloat(w.totalWorkHours)
        : 0;
      sums[i].plannedAbsenceHours += w.plannedAbsenceHours
        ? parseFloat(w.plannedAbsenceHours)
        : 0;
      sums[i].vacationHours += w.vacationHours
        ? parseFloat(w.vacationHours)
        : 0;
      i++;
    }
  }

  const consultantRow = sortedData.map(cons => {
    return (
      <ReconLine
        key={cons.id}
        cons={cons}
        uploadedWorked={uploadedWorked}
        seniorityById={seniorityById}
      />
    );
  });

  // Compute sum of uploaded actual worked hours per displayed week (only when actual data exists)
  const workedSums = yearWeeks.map((_, i) => {
    const yw = yearWeeks[i];
    let total = 0;
    let count = 0;
    for (const cons of filteredData) {
      const weeks = cons.weeks || {};
      const week = weeks[yw] || {};
      const override =
        uploadedWorked[cons.id] && uploadedWorked[cons.id][yw] != null
          ? uploadedWorked[cons.id][yw]
          : null;
      const persisted = week.actualWorked != null ? week.actualWorked : null;
      const value = override != null ? override : persisted;
      if (value != null) {
        total += parseFloat(value) || 0;
        count++;
      }
    }
    return { total, count };
  });

  // Build CSV headers and data for what's currently visible on screen
  const csvHeader = [{ label: "Konsulent", key: "name" }];
  for (const yw of yearWeeks) {
    csvHeader.push({ label: `${yw} Planlagt fakturert`, key: `${yw}_staffed` });
    csvHeader.push({ label: `${yw} Faktisk fakturert`, key: `${yw}_actuals` });
    csvHeader.push({ label: `${yw} Totale arbeidstimer`, key: `${yw}_worked` });
  }

  const csvData = filteredData.map(cons => {
    const row = { name: cons.name };
    for (const yw of yearWeeks) {
      const w = (cons.weeks && cons.weeks[yw]) || {};
      const actuals = w.actuals ? w.actuals : 0;
      const staffed = w.staffed ? w.staffed : 0;
      const persistedWorked = w.actualWorked != null ? w.actualWorked : null;
      const workedOverride =
        uploadedWorked[cons.id] && uploadedWorked[cons.id][yw] != null
          ? uploadedWorked[cons.id][yw]
          : null;
      const worked = workedOverride != null ? workedOverride : persistedWorked; // may be null
      row[`${yw}_staffed`] = format(staffed);
      row[`${yw}_actuals`] = format(actuals);
      row[`${yw}_worked`] = worked != null ? format(worked) : "";
    }
    return row;
  });

  return (
    <div className="module">
      <AppHeading
        departments={departments}
        offset={offset}
        weeks={weeks}
        groups={groups}
        availableOnly={availableOnly}
        seniorities={seniorities}
        serviceareas={serviceareas}
        active={"reconciliation"}
      />
      <AuthorizedContent
        isLoading={!isUserChecked}
        isAuthorized={isAllowed}
        unauthorizedTitle="Ingen tilgang til avstemming"
        unauthorizedMessage="Du har ikke tilgang til denne siden. Ta kontakt med administrator dersom du mener dette er en feil."
        authorizedContent={
          <>
            <FilterSidebarContainer isOpen={isFilterOpen}>
              <CloseButton onClick={() => setIsFilterOpen(false)}>
                <CloseIconStyled />
              </CloseButton>
              <FilteringBar
                selectedDepartments={departmentsArray}
                selectedGroups={groupsArray}
                selectedSeniorities={senioritiesArray}
                selectedServiceAreas={serviceareasArray}
                onNavigate={() => setWaitingForData(true)}
              />
            </FilterSidebarContainer>
            <MainLayout sidebarOpen={isFilterOpen}>
              <ReconContainer>
                <PageHeading
                  departments={departments}
                  offset={offset}
                  weeks={weeks}
                  groups={groups}
                  availableOnly={availableOnly}
                  seniorities={seniorities}
                  serviceareas={serviceareas}
                />
                <FileUpload
                  handleRows={uploadExcelRows}
                  error={error}
                  successMessage={successMessage}
                >
                  Last opp timeføring
                </FileUpload>
                <Spinner show={waitingForData} />
                <h3 className="page-heading">Avstemming</h3>
                <BarContainer>
                  <LeftButtons>
                    <FilteringBarContainer>
                      <FilterButton
                        onClick={() => setIsFilterOpen(!isFilterOpen)}
                        aria-label="Åpne filter"
                        title="Åpne filter"
                      >
                        <FilterIconStyled />
                      </FilterButton>
                    </FilteringBarContainer>
                  </LeftButtons>
                  <CSVLink
                    filename={`Avstemming_${weeks}uker.csv`}
                    data={csvData}
                    headers={csvHeader}
                    separator={";"}
                  >
                    <CSVIconStyled title="Last ned CSV-fil" />
                  </CSVLink>
                </BarContainer>

                <Table
                  inner="reconciliation"
                  sortable={true}
                  onSort={(by, dir) => {
                    setSortBy(by);
                    setSortDirection(dir);
                  }}
                  params={{
                    departments: departments,
                    groups: groups,
                    offset: offset,
                    weeks: weeks,
                    availableOnly: availableOnly,
                    seniorities: seniorities
                  }}
                  yearWeeks={yearWeeks}
                  leadingHeaders={["Antall: " + filteredData.length]}
                >
                  <tbody>{consultantRow}</tbody>
                  <SumFooter>
                    <SumRow className="line">
                      <ConsultantName>Planlagt Fakturert</ConsultantName>
                      {sums.map((sum, i) => (
                        <SumData key={i}>{format(sum.staffed)}</SumData>
                      ))}
                    </SumRow>
                    <SumRow>
                      <ConsultantName>Faktisk Fakturert</ConsultantName>
                      {sums.map((sum, i) => (
                        <SumData key={i}>{format(sum.actuals)}</SumData>
                      ))}
                    </SumRow>
                    <SumRow>
                      <ConsultantName>Totale Arbeidstimer</ConsultantName>
                      {workedSums.map((w, i) => (
                        <SumData key={i}>
                          {w.count ? format(w.total) : "-"}
                        </SumData>
                      ))}
                    </SumRow>
                    <SumRow>
                      <ConsultantName>Leveringgrad</ConsultantName>
                      {sums.map((sum, i) => (
                        <SumData key={i}>
                          {format((sum.actuals / sum.staffed) * 100)}%
                        </SumData>
                      ))}
                    </SumRow>
                    <SumRow>
                      <ConsultantName title="Faktisk faktureringsgrad">
                        Faktisk faktureringsgrad
                      </ConsultantName>
                      {sums.map((sum, i) => (
                        <SumData key={i}>
                          {format(
                            (sum.actuals /
                              (sum.totalAvailableHours -
                                ((sum.plannedAbsenceHours || 0) +
                                  (sum.vacationHours || 0)))) *
                              100
                          )}
                          %
                        </SumData>
                      ))}
                    </SumRow>
                  </SumFooter>
                </Table>
              </ReconContainer>
            </MainLayout>
          </>
        }
      />
    </div>
  );
};

export default Reconciliation;
